import { createTheme, darken, lighten } from '@mui/material';

const CalibriRegularWoff2 = '/fonts/1077505/1d2edbc2-9a79-4dde-b731-13fd5fbec4ff.woff2';
const CalibriRegularWoff = '/fonts/1077505/524709a5-c0e8-4ef7-9cc2-5efe4a4206ed.woff';
const CalibriRegularItalicWoff2 = '/fonts/1077508/23a21260-928c-4393-b2e5-b08ee80f6e33.woff2';
const CalibriRegularItalicWoff = '/fonts/1077508/b365db73-5595-4a49-aeab-9b1ed69dfe8f.woff';
const CalibriBoldWoff2 = '/fonts/1077511/84cf5e03-41e7-494e-953e-6bbcab6d8212.woff2';
const CalibriBoldWoff = '/fonts/1077511/9f41352c-eb11-4896-8988-a749223c4176.woff';
const CalibriBoldItalicWoff2 = '/fonts/1077514/0f0f6bf1-2b1d-4c46-af74-e8ec10faf8f8.woff2';
const CalibriBoldItalicWoff = '/fonts/1077514/68e53bf3-094e-4d3c-aac7-48907752a385.woff';
const CalibriLightWoff2 = '/fonts/5162141/16ce69d2-a210-4f8b-8bd3-a1a7041f71b3.woff2';
const CalibriLightWoff = '/fonts/5162141/489e45af-c988-4abc-9b49-1629e5c316b9.woff';
const CalibriLightItalicWoff2 = '/fonts/5179610/7258b083-5700-466c-995e-b38f0b2ce210.woff2';
const CalibriLightItalicWoff = '/fonts/5179610/9fbdd28c-e1c4-4d20-8062-c843a74ffdd0.woff';
const CarlitoBoldWoff = '/fonts/Carlito-Bold.woff';
const CarlitoBoldItalicWoff = '/fonts/Carlito-BoldItalic.woff';
const CarlitoItalicWoff = '/fonts/Carlito-Italic.woff';
const CarlitoRegularWoff = '/fonts/Carlito-Regular.woff';

const createFontFaceFn = fontFamily => (fontWeight, fontStyle, woff, woff2, extraSources) => {
  const src = [];

  if (extraSources) {
    src.push(...extraSources);
  }

  if (woff2) {
    src.push(`url(${woff2}) format('woff2')`);
  }

  if (woff) {
    src.push(`url(${woff}) format('woff')`);
  }

  return `
    @font-face {
      font-display: swap;
      font-family: '${fontFamily}';
      font-style: ${fontStyle};
      font-weight: ${fontWeight};
      src: ${src.join(', ')};
    }
  `;
};

const createCalibriFontFace = createFontFaceFn('Calibri');
const createCarlitoFontFace = createFontFaceFn('Carlito');
const MuiCssBaseline = {
  styleOverrides: `
    /* Fonts 
    If for some reason fonts will not work properly, please try with this url below;
    @import url('https://fast.fonts.net/lt/1.css?apiType=css&c=26c7e009-d186-4708-bcf1-3f164595eb04&fontids=1077505,1077508,1077511,1077514,5162141,5179610');
    */
    ${createCalibriFontFace(400, 'normal', CalibriRegularWoff, CalibriRegularWoff2)}
    ${createCalibriFontFace(400, 'italic', CalibriRegularItalicWoff, CalibriRegularItalicWoff2)}
    ${createCalibriFontFace(700, 'normal', CalibriBoldWoff, CalibriBoldWoff2)}
    ${createCalibriFontFace(700, 'italic', CalibriBoldItalicWoff, CalibriBoldItalicWoff2)}
    ${createCalibriFontFace(300, 'normal', CalibriLightWoff, CalibriLightWoff2)}
    ${createCalibriFontFace(300, 'italic', CalibriLightItalicWoff, CalibriLightItalicWoff2)}
    ${createCarlitoFontFace(400, 'normal', CarlitoRegularWoff, undefined, [`local('Carlito')`])}
    ${createCarlitoFontFace(400, 'italic', CarlitoItalicWoff, undefined, [`local('Carlito Italic')`, `local('Carlito-Italic')`])}
    ${createCarlitoFontFace(700, 'normal', CarlitoBoldWoff, undefined, [`local('Carlito Bold')`, `local('Carlito-Bold')`])}
    ${createCarlitoFontFace(700, 'italic', CarlitoBoldItalicWoff, undefined, [`local('Carlito Bold Italic')`, `local('Carlito-BoldItalic')`])}
  `
};

const fontSize = 18;
const fontFamily = "'Calibri', 'Carlito', sans-serif";
const htmlFontSize = 16; //it is the default font-size used by browsers.

const fontWeightLight = 300;
const fontWeightRegular = 400;
const fontWeightBold = 700; // @mui/styles/createTypography.js

const caseAllCaps = {
  textTransform: 'uppercase'
};
const underline = {
  textDecoration: 'underline'
};
const coef = fontSize / 18;

const pxToRem = size => `${size / htmlFontSize * coef}rem`;

function round(value) {
  return Math.round(value * 1e5) / 1e5;
}

const pxToEm = (letterSpacing, size) => `${round(letterSpacing / size)}em`;

const buildVariant = (fontWeight, size, lineHeight, letterSpacing, casing, decorating) => Object.assign(Object.assign({
  fontFamily,
  fontWeight,
  fontSize: pxToRem(size),
  // Unitless following https://meyerweb.com/eric/thoughts/2006/02/08/unitless-line-heights/
  lineHeight,
  // The letter spacing was designed for the Roboto font-family. Using the same letter-spacing
  // across font-families can cause issues with the kerning.
  letterSpacing: pxToEm(letterSpacing, size)
}, casing), decorating);

const tonalOffset = 0.2;
const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 960,
      lg: 1200,
      xl: 1920
    }
  },
  components: {
    MuiCssBaseline,
    MuiDialog: {
      styleOverrides: {
        paperWidthXs: {
          maxWidth: '364px'
        },
        paperWidthSm: {
          maxWidth: '558px'
        },
        paperWidthMd: {
          maxWidth: '946px'
        }
      }
    }
  },
  mixins: {
    blur: {
      element: 'blur(4px)',
      background: 'blur(6px)'
    }
  },
  ourShadows: {
    card: '2px 4px 12px rgb(22 24 25 / 12%)',
    overlay: {
      darkBG: '0px 4px 16px rgba(22,24,25,0.48)',
      lightBG: '0px 4px 16px rgba(22,24,25,0.16)'
    },
    dialog: '4px 8px 24px rgba(22,24,25,0.16)'
  },
  palette: {
    background: {
      default: '#0000'
    },
    common: {
      black: '#161819',
      white: '#fff',
      transparent: 'transparent'
    },
    error: {
      dark: '#BE333F',
      main: '#F2616D'
    },
    grey: {
      100: '#F1F6F8',
      200: '#D2D7D9',
      300: '#9AA2A5',
      400: '#6E777B',
      500: '#161819'
    },
    info: {
      main: '#619DF2'
    },
    primary: {
      dark: '#780009',
      light: '#DD3D49',
      main: '#C31924',
      mainGradient: 'linear-gradient(297deg, #DD3D49, #C31924, #780009)'
    },
    success: {
      main: '#43BF58'
    },
    waiting: {
      contrastText: '#fff',
      dark: darken('#EC8F32', tonalOffset),
      light: lighten('#EC8F32', tonalOffset),
      main: '#EC8F32'
    },
    warning: {
      dark: '#EF9E4E',
      main: '#F2CE61'
    },
    tonalOffset
  },
  shape: {
    borderRadius: 4,
    card: {
      borderRadius: 6
    },
    dialog: {
      borderRadius: 8
    }
  },
  transitions: {
    easing: {
      easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)',
      easeOut: 'cubic-bezier(0.0, 0, 0.2, 1)',
      easeIn: 'cubic-bezier(0.4, 0, 1, 1)',
      sharp: 'cubic-bezier(0.4, 0, 0.6, 1)'
    },
    duration: {
      shortest: 150,
      shorter: 200,
      short: 250,
      standard: 300,
      complex: 375,
      enteringScreen: 225,
      leavingScreen: 195
    }
  },
  typography: {
    fontFamily: fontFamily,
    fontSize: fontSize,
    h1: buildVariant(fontWeightLight, 40, 1.2, -0.4),
    h2: buildVariant(fontWeightLight, 32, 1.25, -0.16),
    h3: buildVariant(fontWeightRegular, 24, 1.25, 0),
    h4: buildVariant(fontWeightRegular, 22, 1.27, 0),
    h5: buildVariant(fontWeightLight, 20, 1.3, 0.4),
    h6: buildVariant(fontWeightRegular, 16, 1.375, 0.32, caseAllCaps),
    subtitle1: buildVariant(fontWeightLight, 16, 1.375, 0.48),
    subtitle2: buildVariant(fontWeightRegular, 14, 1.29, 0.28),
    body1: buildVariant(fontWeightRegular, 16, 1.375, 0),
    body2: buildVariant(fontWeightRegular, 14, 1.29, 0),
    button1: buildVariant(fontWeightBold, 16, 1.375, 0.48, caseAllCaps),
    button2: buildVariant(fontWeightBold, 14, 1.29, 0.42, caseAllCaps),
    caption: buildVariant(fontWeightRegular, 12, 1.5, 0),
    overline: buildVariant(fontWeightRegular, 12, 1.5, 0.36, caseAllCaps),
    textLink: buildVariant(fontWeightBold, 16, 1.375, 0.48, null, underline)
  }
});
theme.typography.pxToRem = pxToRem;
theme.typography.h1 = Object.assign(Object.assign({}, theme.typography.h1), {
  [theme.breakpoints.up('sm')]: {
    fontSize: pxToRem(60),
    letterSpacing: pxToEm(-0.6, 60),
    lineHeight: 1.13
  },
  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(68),
    letterSpacing: pxToEm(-0.68, 68),
    lineHeight: 1.06
  }
});
theme.typography.h2 = Object.assign(Object.assign({}, theme.typography.h2), {
  [theme.breakpoints.up('sm')]: {
    fontSize: pxToRem(40),
    letterSpacing: pxToEm(-0.24, 40),
    lineHeight: 1.2
  },
  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(48),
    letterSpacing: pxToEm(-0.24, 48),
    lineHeight: 1.125
  }
});
theme.typography.h3 = Object.assign(Object.assign({}, theme.typography.h3), {
  [theme.breakpoints.up('sm')]: {
    fontSize: pxToRem(28),
    lineHeight: 1.21
  },
  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(32),
    lineHeight: 1.25
  }
});
theme.typography.h4 = Object.assign(Object.assign({}, theme.typography.h4), {
  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(24),
    lineHeight: 1.25
  }
});
theme.typography.h5 = Object.assign(Object.assign({}, theme.typography.h5), {
  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(22),
    letterSpacing: pxToEm(0.66, 22),
    lineHeight: 1.27
  }
});
theme.typography.h6 = Object.assign(Object.assign({}, theme.typography.h6), {
  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(18),
    letterSpacing: pxToEm(0.32, 18),
    lineHeight: 1.33
  }
});
theme.typography.subtitle1 = Object.assign(Object.assign({}, theme.typography.subtitle1), {
  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(18),
    letterSpacing: pxToEm(0.32, 18),
    lineHeight: 1.33
  }
});
theme.typography.subtitle2 = Object.assign(Object.assign({}, theme.typography.subtitle2), {
  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(16),
    letterSpacing: pxToEm(0, 16),
    lineHeight: 1.33
  }
});
theme.typography.body1 = Object.assign(Object.assign({}, theme.typography.body1), {
  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(18),
    lineHeight: 1.33
  }
});
theme.typography.body2 = Object.assign(Object.assign({}, theme.typography.body2), {
  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(16),
    lineHeight: 1.25
  }
});
theme.typography.button1 = Object.assign(Object.assign({}, theme.typography.button1), {
  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(18),
    letterSpacing: pxToEm(0.54, 18),
    lineHeight: 1.33
  }
});
theme.typography.button2 = Object.assign(Object.assign({}, theme.typography.button2), {
  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(14),
    lineHeight: 1.29
  },
  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(16),
    letterSpacing: pxToEm(0.48, 16),
    lineHeight: 1.25
  }
});
theme.typography.overline = Object.assign(Object.assign({}, theme.typography.overline), {
  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(14),
    letterSpacing: pxToEm(0.42, 14),
    lineHeight: 1.43
  }
});
theme.typography.textLink = Object.assign(Object.assign({}, theme.typography.textLink), {
  [theme.breakpoints.up('md')]: {
    fontSize: pxToRem(18),
    letterSpacing: pxToEm(0.54, 18),
    lineHeight: 1.33
  }
});
theme.zIndex.snackbar = 100000;
theme.components = Object.assign(Object.assign({}, theme.components), {
  MuiButton: {
    defaultProps: {
      modifier: 'light',
      variant: 'text'
    },
    styleOverrides: {
      root: Object.assign(Object.assign({}, theme.typography.button1), {
        backgroundRepeat: 'no-repeat',
        backgroundSize: '100% 200%',
        borderRadius: theme.shape.borderRadius,
        borderStyle: 'solid',
        borderWidth: '1px',
        minHeight: theme.spacing(5),
        minWidth: theme.spacing(15),
        transition: 'all 0.3s',
        '&:hover, &:focus': {
          backgroundPosition: '0 100%'
        }
      })
    },
    variants: [{
      props: {
        modifier: 'light'
      },
      style: {
        color: theme.palette.grey[500]
      }
    }, {
      props: {
        modifier: 'dark'
      },
      style: {
        color: theme.palette.common.white
      }
    }, {
      props: {
        variant: 'primary'
      },
      style: {
        backgroundImage: `linear-gradient(to top, ${theme.palette.common.white} 50%, ${theme.palette.primary.main} 50%)`,
        borderColor: theme.palette.primary.main,
        color: theme.palette.common.white,
        '&:hover, &:focus': {
          color: theme.palette.primary.main
        },
        '&:disabled': {
          backgroundColor: theme.palette.grey[300],
          backgroundImage: 'none',
          borderColor: theme.palette.grey[300],
          color: theme.palette.common.white
        }
      }
    }, {
      props: {
        variant: 'secondary'
      },
      style: {
        '&:disabled': {
          backgroundImage: 'none',
          borderColor: theme.palette.grey[300],
          color: theme.palette.grey[300]
        }
      }
    }, {
      props: {
        variant: 'secondary',
        modifier: 'dark'
      },
      style: {
        backgroundImage: `linear-gradient(to top, ${theme.palette.common.white} 50%, ${theme.palette.common.transparent} 50%)`,
        borderColor: theme.palette.common.white,
        color: theme.palette.common.white,
        '&:hover, &:focus': {
          color: theme.palette.grey[500]
        }
      }
    }, {
      props: {
        variant: 'secondary',
        modifier: 'light'
      },
      style: {
        backgroundImage: `linear-gradient(to top, ${theme.palette.grey[500]} 50%, ${theme.palette.common.transparent} 50%)`,
        borderColor: theme.palette.grey[500],
        color: theme.palette.grey[500],
        '&:hover, &:focus': {
          color: theme.palette.common.white
        }
      }
    }, {
      props: {
        variant: 'tertiary'
      },
      style: {
        backdropFilter: 'blur(4px)',
        backgroundImage: `linear-gradient(to top, rgba(110, 119, 120, 0.4) 50%, rgba(22, 24, 25, 0.4) 50%)`,
        border: 'none',
        color: theme.palette.common.white,
        '&:active': {
          border: `1px solid ${theme.palette.common.white}`
        },
        '& [class*=rippleVisible]': {
          color: theme.palette.grey[500]
        },
        '&:disabled': {
          backgroundColor: 'rgba(241, 246, 248, 0.4)',
          backgroundImage: 'none',
          color: theme.palette.grey[200]
        }
      }
    }, {
      props: {
        variant: 'text'
      },
      style: {
        borderColor: theme.palette.common.transparent,
        '&:hover, &:focus': {
          backgroundColor: theme.palette.common.transparent,
          color: theme.palette.primary.main
        },
        '&:disabled': {
          color: theme.palette.grey[300]
        }
      }
    }, {
      props: {
        variant: 'text',
        modifier: 'dark'
      },
      style: {
        color: '#fff'
      }
    }, {
      props: {
        variant: 'text',
        modifier: 'light'
      },
      style: {
        color: '#161819'
      }
    }]
  },
  MuiFormHelperText: {
    styleOverrides: {
      root: {
        margin: `${theme.spacing(0.375)} 0 0`
      }
    }
  },
  MuiInputLabel: {
    styleOverrides: {
      outlined: ({
        ownerState
      }) => Object.assign(Object.assign(Object.assign(Object.assign({}, theme.typography.body1), {
        transform: `translate(${theme.spacing(2)}, ${theme.spacing(1.1)}) scale(1)`
      }), ownerState.shrink && {
        [theme.breakpoints.up('xs')]: Object.assign(Object.assign({}, theme.typography.caption), {
          transform: `translate(${theme.spacing(2)}, ${theme.spacing(-1)}) scale(1)`
        })
      }), {
        [theme.breakpoints.up('md')]: {
          top: theme.spacing(-0.125)
        }
      })
    }
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: Object.assign({}, theme.typography.body1),
      inputSizeSmall: {
        padding: `${theme.spacing(1.063125)} ${theme.spacing(2)}`,
        [theme.breakpoints.up('md')]: {
          padding: `${theme.spacing(0.88375)} ${theme.spacing(2)}`
        }
      },
      notchedOutline: {
        padding: theme.spacing(0, 1.25),
        '& > legend': Object.assign({}, theme.typography.caption)
      }
    }
  },
  MuiFormControl: {
    styleOverrides: {
      root: {
        // OVERRIDE DEFAULT LIGHT THEME
        '.MuiInputLabel-outlined': {
          color: theme.palette.grey[300],
          '&.Mui-disabled': {
            color: theme.palette.grey[200]
          },
          '&.Mui-error': {
            color: theme.palette.grey[300]
          },
          '&.MuiInputLabel-shrink': {
            color: theme.palette.common.black,
            '&.Mui-disabled': {
              color: theme.palette.grey[300]
            },
            '&.Mui-error': {
              color: theme.palette.common.black
            }
          }
        },
        '.MuiOutlinedInput-root': {
          input: {
            color: theme.palette.common.black
          },
          fieldset: {
            borderColor: theme.palette.grey[300]
          },
          '& .MuiSelect-icon': {
            color: theme.palette.grey[500]
          },
          '& .MuiSelect-select': {
            color: theme.palette.grey[500]
          },
          '&:hover': {
            fieldset: {
              borderColor: theme.palette.grey[500]
            },
            '& .MuiSelect-icon': {
              color: theme.palette.grey[500]
            }
          },
          '&.Mui-focused': {
            fieldset: {
              borderColor: theme.palette.grey[500],
              borderWidth: 2
            },
            '& .MuiSelect-icon': {
              color: theme.palette.grey[500]
            }
          },
          '&.Mui-disabled': {
            fieldset: {
              borderColor: theme.palette.grey[200]
            },
            input: {
              textFillColor: theme.palette.grey[300]
            },
            '& .MuiSelect-icon': {
              color: theme.palette.grey[200]
            },
            '& .MuiSelect-select': {
              textFillColor: theme.palette.grey[300]
            }
          },
          '&.Mui-error': {
            fieldset: {
              borderColor: theme.palette.error.main,
              '&:focus': {
                borderWidth: 2
              }
            },
            input: {
              color: theme.palette.grey[500]
            },
            '& .MuiSelect-icon': {
              color: theme.palette.grey[500]
            }
          }
        },
        '& .MuiFormHelperText-root.Mui-error': {
          color: theme.palette.grey[500]
        },
        // OVERRIDE DEFAULT DARK THEME
        '&.MuiFormControl-root--dark-modifier .MuiInputLabel-root, &[modifier="dark"] .MuiInputLabel-root': {
          color: theme.palette.grey[200],
          '&.Mui-disabled': {
            color: theme.palette.grey[400]
          },
          '&.Mui-error': {
            color: theme.palette.grey[200]
          },
          '&.MuiInputLabel-shrink': {
            color: theme.palette.common.white,
            '&.Mui-disabled': {
              color: theme.palette.grey[300]
            },
            '&.Mui-error': {
              color: theme.palette.common.white
            }
          }
        },
        '&.MuiFormControl-root--dark-modifier .MuiOutlinedInput-root,  &[modifier="dark"] .MuiOutlinedInput-root': {
          input: {
            color: theme.palette.common.white
          },
          fieldset: {
            borderColor: theme.palette.grey[300]
          },
          '& .MuiSelect-icon': {
            color: theme.palette.common.white
          },
          '& .MuiSelect-select': {
            color: theme.palette.common.white
          },
          '&:hover': {
            fieldset: {
              borderColor: theme.palette.common.white
            },
            '& .MuiSelect-icon': {
              color: theme.palette.common.white
            }
          },
          '&.Mui-focused': {
            fieldset: {
              borderColor: theme.palette.common.white,
              borderWidth: 2
            },
            '& .MuiSelect-icon': {
              color: theme.palette.common.white
            }
          },
          '&.Mui-disabled': {
            fieldset: {
              borderColor: theme.palette.grey[400]
            },
            input: {
              textFillColor: theme.palette.grey[300]
            },
            '& .MuiSelect-icon': {
              color: theme.palette.grey[400]
            },
            '& .MuiSelect-select': {
              textFillColor: theme.palette.grey[300]
            }
          },
          '&.Mui-error': {
            fieldset: {
              borderColor: theme.palette.error.main,
              '&:focus': {
                borderWidth: 2
              }
            },
            input: {
              color: theme.palette.common.white
            }
          }
        },
        '&.MuiFormControl-root--dark-modifier .MuiFormHelperText-root.Mui-error, &[modifier="dark"] .MuiFormHelperText-root.Mui-error': {
          color: theme.palette.common.white
        },
        '&.MuiFormControl-root--dark-modifier .MuiInputLabel-root, &[modifier="dark"]': {
          // Override for font-awesome icons that are inside a dark form control
          '.svg-inline--fa': {
            color: theme.palette.grey[100]
          }
        },
        // EXTRA OVERRIDES
        '.MuiSelect-select': {
          position: 'relative',
          top: theme.spacing(0.125)
        }
      }
    }
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        // Overrides for the DatePicker paper component which is inside MuiPaper
        '.MuiDayPicker-weekDayLabel': {
          fontSize: pxToRem(14)
        },
        '.MuiPickersDay-root': {
          fontSize: pxToRem(14)
        },
        '.MuiPickersDay-root.Mui-selected:focus': {
          backgroundColor: theme.palette.primary.main,
          border: `2px solid ${theme.palette.primary.light}`
        }
      }
    }
  }
});

export { theme as default };
